<script setup lang="ts">
    import { computed, onBeforeMount, onBeforeUnmount, ref, toRefs } from "vue";
    import emitter from "tiny-emitter/instance";
    import { AxiosError } from "axios";
    import store from "@/store";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import { useKForm } from "@/composables/useKForm";
    import { NumberValidationRule } from "@/components/forms/inputs/Number/numberValidation";
    import { Besokstypkod } from "@/tabs/Oppenvard/types";

    const props = defineProps<{
        tabId: number;
        parameters?: {
            besokstypkodId: number;
        };
    }>();

    const { parameters, tabId } = toRefs(props);

    const { inputErrors, formErrors, handleErrorResponse } = useKForm();

    const formRef = ref();

    const besokstypkod = computed(() =>
        store.getters["besokstypkod/besokstypkodById"]({
            id: parameters?.value?.besokstypkodId,
        })
    );

    const isEditing = computed(() => !!besokstypkod.value);

    const hasUnsavedData = computed(() => formRef.value?.hasUnsavedData());

    defineExpose({
        hasUnsavedData,
        tabId,
    });

    const setTabName = () => {
        store.commit("tabs/setTabName", {
            id: tabId.value,
            name: isEditing.value ? "Redigera besökstypkod" : "Lägg till besökstypkod",
        });
    };

    // TODO convert to useTabs in v3
    const closeTab = () => {
        if (formRef.value.hasUnsavedData()) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId.value);
                        },
                    },
                ],
            });

            return;
        }

        store.dispatch("tabs/closeTab", tabId.value);
    };

    const onSubmit = async (besokstypkod: Besokstypkod) => {
        try {
            if (isEditing.value) {
                await store.dispatch("besokstypkod/updateBesokstypkod", {
                    besokstypkod,
                });
            } else {
                await store.dispatch("besokstypkod/createBesokstypkod", {
                    besokstypkod,
                });
            }

            // TODO: Det borde inte behöva göras en ny hämtning manuellt, men av någon anledning fylls inte
            // nuvarande filter på med nya objekt. (Funkar nedanstående även utan page och limit?)
            await store.dispatch("besokstypkod/fetchBesokstypkoder", {});

            closeTab();
        } catch (thrownError) {
            const error = thrownError as AxiosError;

            handleErrorResponse(error);
        }
    };

    const fetchFormData = async () =>
        besokstypkod.value ?? {
            ordning: 0,
            is_active: true,
        };

    onBeforeMount(async () => {
        await store.dispatch("besokstypkod/addSubscriber");

        setTabName();
    });

    onBeforeUnmount(async () => {
        await store.dispatch("besokstypkod/removeSubscriber");
    });
</script>

<template>
    <div class="besokstypkod-form">
        <KForm
            ref="formRef"
            id="besokstypkod"
            :initialValuesGetter="fetchFormData"
            :submit="onSubmit"
            submitContent="Spara och stäng"
            cancelContent="Avbryt"
            :errors="inputErrors"
            :formErrors="formErrors"
            @cancel="closeTab"
        >
            <template #header>
                <AdminLockBorder />
                <h2 v-if="isEditing">Redigera besökstypkod</h2>
                <h2 v-else>Lägg till besökstypkod</h2>
            </template>
            <template #default>
                <div class="form-content">
                    <KText name="code" label="Kod" validation="required" />
                    <KText name="displayName" label="Namn" validation="required" />
                    <KText type="textarea" name="description" label="Beskrivning" class="span-two" />
                    <KDateTimepicker name="valid_from_date" label="Giltig från" validation="required" />
                    <KDateTimepicker name="valid_to_date" label="Giltig till" validation="required" />
                    <KNumber name="ordning" label="Ordning" :validation="[[NumberValidationRule.Min, 0]]" />
                    <KCheckbox name="is_active" label="Aktiv" valueType="boolean" valign="center" />
                </div>
            </template>
        </KForm>
    </div>
</template>

<style lang="scss" scoped></style>
